import { Button } from "@client/components/button/Button";
import { Card } from "@client/components/card/Card";
import { signInWithGoogle } from "@client/lib/auth-client";
import { useEffect, useState } from "react";

interface AuthError {
	error: string;
	message: string;
	details: string;
	status: number;
	statusText: string;
}

export function LoginPage() {
	const [isSigningIn, setIsSigningIn] = useState(false);
	const [authError, setAuthError] = useState<AuthError | null>(null);

	useEffect(() => {
		console.log("🔍 LoginPage: Component mounted");

		// Check if we're on a callback URL
		const currentUrl = window.location.href;
		console.log("🔍 LoginPage: Current URL:", currentUrl);

		if (currentUrl.includes("/api/auth/callback")) {
			console.log(
				"🚨 LoginPage: We're on a callback URL but showing login page - this indicates an auth flow issue",
			);
		}
	}, []);

	const handleGoogleSignIn = async () => {
		console.log("🔍 LoginPage: Starting Google sign-in process");
		setIsSigningIn(true);
		setAuthError(null); // Clear any previous errors

		try {
			console.log("🔍 LoginPage: Calling signInWithGoogle");
			const result = await signInWithGoogle("/");

			console.log("🔍 LoginPage: Sign-in result:", {
				result,
				timestamp: new Date().toISOString(),
			});

			if (result.error) {
				// Handle structured error response
				console.error("🚨 LoginPage: Authentication failed:", result.error);
				setAuthError(result.error);
				setIsSigningIn(false);
			} else {
				// Success case - the redirect should happen automatically
				console.log("✅ LoginPage: Authentication successful");
			}
		} catch (error) {
			// Fallback error handling for unexpected errors
			console.error("🚨 LoginPage: Unexpected sign in error:", {
				error: error instanceof Error ? error.message : "Unknown error",
				stack: error instanceof Error ? error.stack : undefined,
				timestamp: new Date().toISOString(),
			});

			setAuthError({
				error: "Unexpected Error",
				message:
					error instanceof Error
						? error.message
						: "An unexpected error occurred",
				details: "Please try refreshing the page and signing in again",
				status: 500,
				statusText: "Internal Error",
			});
			setIsSigningIn(false);
		}
	};

	const dismissError = () => {
		setAuthError(null);
	};

	return (
		<div className="min-h-dvh flex flex-col items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
			<div className="w-full max-w-md mx-auto">
				{/* Header */}
				<div className="text-center mb-8">
					
					<h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">

						<span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
							AI SDR Demo
						</span>
					</h1>
					<p className="mt-4 text-lg text-gray-600 dark:text-gray-300">
						Sign in to start building your AI Sales Representative
					</p>
				</div>

				{/* Error Alert */}
				{authError && (
					<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
						<div className="flex items-start justify-between">
							<div className="flex items-start space-x-3">
								<div className="flex-shrink-0">
									<svg
										className="w-5 h-5 text-red-400"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="flex-1">
									<h3 className="text-sm font-medium text-red-800 dark:text-red-200">
										{authError.error}
									</h3>
									<p className="mt-1 text-sm text-red-700 dark:text-red-300">
										{authError.message}
									</p>
									<p className="mt-1 text-xs text-red-600 dark:text-red-400">
										{authError.details}
									</p>
									{authError.status !== 500 && (
										<p className="mt-1 text-xs text-red-500 dark:text-red-500">
											Status: {authError.status} {authError.statusText}
										</p>
									)}
								</div>
							</div>
							<button
								onClick={dismissError}
								className="flex-shrink-0 ml-3 text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
							>
								<span className="sr-only">Dismiss error</span>
								<svg
									className="w-4 h-4"
									viewBox="0 0 20 20"
									fill="currentColor"
								>
									<path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
								</svg>
							</button>
						</div>
						<div className="mt-3">
							<button
								onClick={handleGoogleSignIn}
								disabled={isSigningIn}
								className="text-xs text-red-700 dark:text-red-300 underline hover:text-red-900 dark:hover:text-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Try again
							</button>
						</div>
					</div>
				)}

				{/* Login Card */}
				<Card className="p-8">
					<div className="space-y-6">
						<div className="text-center">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
								Sign in to continue
							</h2>
							<p className="text-sm text-gray-600 dark:text-gray-400">
								Use your Google account to access the platform
							</p>
						</div>

						<Button
							onClick={handleGoogleSignIn}
							disabled={isSigningIn}
							className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 font-medium py-3 px-4 flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
							size="lg"
						>
							<svg className="w-5 h-5" viewBox="0 0 24 24">
								<path
									fill="#4285F4"
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
								/>
								<path
									fill="#34A853"
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
								/>
								<path
									fill="#FBBC05"
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
								/>
								<path
									fill="#EA4335"
									d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
								/>
							</svg>
							{isSigningIn ? (
								<>
									<svg
										className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Signing in...
								</>
							) : (
								"Continue with Google"
							)}
						</Button>

						<div className="text-center text-xs text-gray-500 dark:text-gray-400">
							By signing in, you agree to our terms of service and privacy
							policy.
						</div>
					</div>
				</Card>

				{/* Features */}
				<div className="mt-8 text-center">
					<h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
						What you'll get access to:
					</h3>
					<div className="grid grid-cols-1 gap-2 text-sm text-gray-600 dark:text-gray-400">
						<div className="flex items-center justify-center gap-2">
							<span className="text-green-500">✓</span>
							Build & deploy AI sales representatives
						</div>
						<div className="flex items-center justify-center gap-2">
							<span className="text-green-500">✓</span>
							Real-time conversation training
						</div>
						<div className="flex items-center justify-center gap-2">
							<span className="text-green-500">✓</span>
							Performance analytics & insights
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
